import React from 'react'
import styled from 'styled-components'
import { useForm, Controller } from 'react-hook-form'
import { useToasts } from 'react-toast-notifications'
import {
  useProfile,
  useLocalLabels,
  useSubscribeToNewsletter,
} from '@sentecacommerce-theme/lib'
import {
  Input,
  InputLabel as Label,
  FormError,
  Checkbox,
  Button,
  Select,
  RadioInput,
  Copy,
} from '../../Common'
import { SubscribeForNewsletterLabels } from '../subscribeForNewsletter.props'

export const Form = () => {
  const { data } = useProfile()
  const labels = useLocalLabels<SubscribeForNewsletterLabels>()
  const getDefaultValues = () => {
    if (!data) {
      return {
        names: '',
        gender: -1,
        phone: '',
        email: '',
        city: '',
        address: '',
        childrenCount: '',
        gdprConsent: false,
        ageConsent: false,
        privacyConsent: false,
      }
    }

    return {
      names: data.firstName ? `${data.firstName} ${data.lastName || ''}` : '',
      gender: +(data.gender || '-1'),
      phone: data.phoneNumber || '',
      email: data.email || '',
      city: '',
      address: '',
      childrenCount: '',
      gdprConsent: false,
      ageConsent: false,
      privacyConsent: false,
    }
  }

  const { addToast } = useToasts()

  const { subscribe } = useSubscribeToNewsletter({
    callback: () => {
      addToast(labels?.newsletterSubscriptionSuccess, {
        appearance: 'success',
        autoDismiss: true,
        id: 'custom-id',
      })
    },
  })

  const { control, errors, handleSubmit } = useForm({
    defaultValues: getDefaultValues(),
    mode: 'onChange',
  })

  const onSubmit = ({ email }: { email: string }) => {
    subscribe(email)
  }

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <FormGroup half>
        <Label htmlFor="names">
          {`${labels?.namesInputLabel} *`}
          <Controller
            control={control}
            name="names"
            rules={{
              required: labels?.fieldIsRequired,
            }}
            as={<Input id="contact-form-names" />}
          />
          <FormError errors={errors} fieldName="names" />
        </Label>
      </FormGroup>
      <FormGroup half>
        <Label htmlFor="email">
          {labels?.emailInputLabel} *
          <Controller
            control={control}
            name="email"
            rules={{
              required: labels?.fieldIsRequired,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: labels?.invalidEmail,
              },
            }}
            as={<Input id="contact-form-email" />}
          />
          <FormError errors={errors} fieldName="email" />
        </Label>
      </FormGroup>
      <FormGroup half>
        <Label htmlFor="phone">
          {labels?.phoneInputLabel} *
          <Controller
            control={control}
            name="phone"
            rules={{
              required: labels?.fieldIsRequired,
            }}
            as={<Input id="contact-form-phone" />}
          />
          <FormError errors={errors} fieldName="phone" />
        </Label>
      </FormGroup>
      <FormGroup half>
        <Label htmlFor="city">
          {labels?.cityInputLabel} *
          <Controller
            control={control}
            name="city"
            rules={{
              required: labels?.fieldIsRequired,
            }}
            as={<Input id="contact-form-city" />}
          />
          <FormError errors={errors} fieldName="city" />
        </Label>
      </FormGroup>
      <FormGroup half>
        <Label htmlFor="address">
          {labels?.addressInputLabel} *
          <Controller
            control={control}
            name="address"
            rules={{
              required: labels?.fieldIsRequired,
            }}
            as={<Input id="contact-form-address" />}
          />
          <FormError errors={errors} fieldName="address" />
        </Label>
      </FormGroup>
      <FormGroup half>
        <Label htmlFor="childrenCount">
          {labels?.childrenCountInputLabel} *
          <Controller
            control={control}
            name="childrenCount"
            rules={{
              required: labels?.fieldIsRequired,
            }}
            render={({ value, onChange }) => (
              <Select
                placeholder={labels?.selectChildrenCountLabel}
                value={value}
                onChange={onChange}
                options={new Array(5).fill(1).map((_, index) => {
                  return {
                    label: index + 1,
                    value: index + 1,
                  }
                })}
              />
            )}
          />
          <FormError errors={errors} fieldName="childrenCount" />
        </Label>
      </FormGroup>
      <FormGroup half>
        <Label htmlFor="">
          {labels?.genderInputLabel} *
          <Controller
            control={control}
            name="gender"
            rules={{
              required: labels?.fieldIsRequired,
            }}
            render={({ value, onChange }) => (
              <div className="mt-2 d-flex">
                <div className="mr-3">
                  <RadioInput
                    name="gender"
                    value={1}
                    isChecked={value === 1}
                    onChange={onChange}
                    label={labels?.maleLabel}
                  />
                </div>
                <RadioInput
                  name="gender"
                  value={2}
                  isChecked={value === 2}
                  onChange={onChange}
                  label={labels?.femaleLabel}
                />
              </div>
            )}
          />
          <FormError errors={errors} fieldName="gender" />
        </Label>
      </FormGroup>
      <FormGroup>
        <Label htmlFor="">
          <GDPRContainer>
            <Controller
              control={control}
              name="gdprConsent"
              rules={{
                required: labels?.fieldIsRequired,
              }}
              render={({ value, onChange }) => (
                <Checkbox
                  onChange={() => onChange(!value)}
                  value={value}
                  isChecked={value}
                  small
                  align="flex-start"
                  name="contact-form-gdprConsent"
                />
              )}
            />
            <GDPRLabel>
              <Copy html={labels?.gdprCheckboxLabel} />
            </GDPRLabel>
          </GDPRContainer>
          <FormError errors={errors} fieldName="gdprConsent" />
        </Label>
      </FormGroup>
      <FormGroup>
        <Label htmlFor="">
          <GDPRContainer>
            <Controller
              control={control}
              name="ageConsent"
              rules={{
                required: labels?.fieldIsRequired,
              }}
              render={({ value, onChange }) => (
                <Checkbox
                  onChange={() => onChange(!value)}
                  value={value}
                  isChecked={value}
                  small
                  align="flex-start"
                  name="contact-form-ageConsent"
                />
              )}
            />
            <GDPRLabel>
              <Copy html={labels?.ageLimitCheckboxLabel} />
            </GDPRLabel>
          </GDPRContainer>
          <FormError errors={errors} fieldName="ageConsent" />
        </Label>
      </FormGroup>
      <FormGroup>
        <Label htmlFor="">
          <GDPRContainer>
            <Controller
              control={control}
              name="privacyConsent"
              rules={{
                required: labels?.fieldIsRequired,
              }}
              render={({ value, onChange }) => (
                <Checkbox
                  onChange={() => onChange(!value)}
                  value={value}
                  isChecked={value}
                  small
                  align="flex-start"
                  name="contact-form-privacyConsent"
                />
              )}
            />
            <GDPRLabel>
              <Copy html={labels?.subscribeCheckboxLabel} />
            </GDPRLabel>
          </GDPRContainer>
          <FormError errors={errors} fieldName="privacyConsent" />
        </Label>
      </FormGroup>
      <FormGroup>
        <ButtonContainer>
          <Button type="submit" variant="green">
            {labels?.submitCTALabel}
          </Button>
        </ButtonContainer>
      </FormGroup>
    </StyledForm>
  )
}

const StyledForm = styled.form.attrs({
  className: 'row',
})``

const FormGroup = styled.div.attrs(({ half }: any) => ({
  className: half ? 'col-6' : 'col-12',
}))<{ half?: boolean }>``

const GDPRLabel = styled.p`
  max-width: calc(100% - 2.8rem);
  margin-top: -0.3rem;

  & span {
    color: ${({ theme }) => theme.palette.silverTree};
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
    &:after,
    &:before {
      content: ')';
      color: ${({ theme }) => theme.palette.greyTitle};
    }

    &:before {
      content: ' (';
      color: ${({ theme }) => theme.palette.greyTitle};
    }
  }
`

const ButtonContainer = styled.div.attrs({
  className: 'mt-4',
})`
  display: flex;
  align-items: center;
  justify-content: center;
`

const GDPRContainer = styled.div`
  display: flex;

  & p,
  span,
  div {
    color: ${({ theme }) => theme.palette.primary};
  }

  & svg {
    color: ${({ theme }) => theme.palette.silverTree};
  }
`
